// ===== File: src/pages/Listing/ContractorListingGridSkeleton.tsx =====
import React from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';
import LoadingOverlay from '../../Components/LoadingOverlay';

interface ContractorListingGridSkeletonProps {
  // No props needed - responsive design handled internally
}

const ContractorCardSkeleton: React.FC = () => (
  <Card className="h-100 shadow-sm">
    <CardBody>
      {/* Header section with avatar and title */}
      <div className="d-flex mb-3">
        <div 
          className="flex-shrink-0 me-3"
          style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#e9ecef',
            borderRadius: '50%',
            animation: 'skeleton-pulse 1.5s ease-in-out infinite'
          }}
        />
        <div className="flex-grow-1">
          {/* Business name */}
          <div 
            className="mb-2"
            style={{
              width: '70%',
              height: '20px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              animation: 'skeleton-pulse 1.5s ease-in-out infinite'
            }}
          />
          {/* Rating */}
          <div 
            style={{
              width: '80px',
              height: '16px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              animation: 'skeleton-pulse 1.5s ease-in-out infinite'
            }}
          />
        </div>
      </div>

      {/* Contact info section */}
      <div className="mb-3">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="d-flex align-items-center mb-2">
            <div 
              className="me-2"
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: '#e9ecef',
                borderRadius: '2px',
                animation: 'skeleton-pulse 1.5s ease-in-out infinite'
              }}
            />
            <div 
              style={{
                width: `${Math.random() * 80 + 100}px`, // Random width
                height: '14px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px',
                animation: 'skeleton-pulse 1.5s ease-in-out infinite'
              }}
            />
          </div>
        ))}
      </div>

      {/* Categories badges */}
      <div className="mb-3">
        <div className="d-flex flex-wrap gap-1">
          {[...Array(3)].map((_, index) => (
            <div 
              key={index}
              style={{
                width: `${Math.random() * 40 + 60}px`, // Random width for badges
                height: '24px',
                backgroundColor: '#e9ecef',
                borderRadius: '12px',
                animation: 'skeleton-pulse 1.5s ease-in-out infinite'
              }}
            />
          ))}
        </div>
      </div>

      {/* Description */}
      <div className="mb-3">
        {[...Array(3)].map((_, index) => (
          <div 
            key={index}
            className="mb-1"
            style={{
              width: index === 2 ? '60%' : '100%', // Last line shorter
              height: '14px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              animation: 'skeleton-pulse 1.5s ease-in-out infinite'
            }}
          />
        ))}
      </div>
    </CardBody>
  </Card>
);

const ContractorListingGridSkeleton: React.FC<ContractorListingGridSkeletonProps> = () => {
  return (
    <>
      {/* Loading Overlay */}
      <div className="text-center py-5">
        <LoadingOverlay
          isVisible={true}
          size="medium"
          noBlur={true}
          text="BidBro"
          subtext="Loading contractors..."
          backgroundOpacity={0.4}
          debug={false}
        />
      </div>

      {/* Skeleton Cards */}
      <style>
        {`
          @keyframes skeleton-pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.6;
            }
          }
        `}
      </style>
      <Row className="g-3">
        {/* Desktop: 9 listings (3 rows × 3 columns) */}
        {[...Array(9)].map((_, index) => (
          <Col 
            key={index} 
            xs={12}      // Mobile: 1 column (but we'll show only 3)
            md={6}       // Tablet: 2 columns (but we'll show only 6) 
            lg={4}       // Desktop: 3 columns (show all 9)
            className={`
              ${index >= 3 ? 'd-none d-md-block' : ''} 
              ${index >= 6 ? 'd-none d-lg-block' : ''}
            `}
          >
            <ContractorCardSkeleton />
          </Col>
        ))}
      </Row>
    </>
  );
};

export default ContractorListingGridSkeleton;